import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { functionNoop } from 'src/utils/function/noop';
import { animated, useSpring, useSpringRef } from '@react-spring/web';

import { FLOAT_ANIMATION_CONFIG } from 'src/document-editor/neue-bubble-menu-fixed';
import { useUpdateEffect } from 'react-use';

type LayoutStyle = 'desktop' | 'mobile';

type Props = {
  title?: string;
  subtitle?: JSX.Element;
  width?: string;
  layoutStyle?: LayoutStyle;
  overlayClassName?: string;
  openRef: React.MutableRefObject<(() => void) | undefined>;
  closeRef?: React.MutableRefObject<(() => void) | undefined>;
  children: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  initialFocus?: React.RefObject<HTMLElement>;
};

export const MiniModal = ({
  title,
  subtitle,
  layoutStyle = 'desktop',
  openRef,
  width = '512px',
  overlayClassName,
  closeRef,
  children,
  initialFocus,
  onOpen = functionNoop,
  onClose = functionNoop,
}: Props) => {
  const [opened, setOpened] = useState(false);
  const internalCloseRef = useRef<() => void>();
  const activeCloseRef = closeRef || internalCloseRef;

  useEffect(() => {
    openRef.current = () => {
      setOpened(true);
      onOpen();
    };
    activeCloseRef.current = () => {
      setOpened(false);
      onClose && onClose();
    };
  }, [openRef, onOpen]);

  const onDialogClose = () => {
    activeCloseRef.current?.();
  };

  useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && opened) {
        event.preventDefault();
        event.stopPropagation();
        onDialogClose();
      }
    };
    window.addEventListener('keydown', onWindowKeyDown);
    return () => {
      window.removeEventListener('keydown', onWindowKeyDown);
    };
  }, [opened]);

  const api = useSpringRef();
  const [styles] = useSpring(
    () => ({
      ref: api,
      from: { y: 8, opacity: 0 },
      to: { y: 0, opacity: 1 },
    }),
    []
  );

  useUpdateEffect(() => {
    if (opened) {
      api.start({
        config: FLOAT_ANIMATION_CONFIG,
        to: { y: 0, opacity: 1 },
      });
    } else {
      api.start({
        to: { y: 8, opacity: 0 },
      });
    }
  }, [opened]);

  return (
    <Transition show={opened} as={Fragment}>
      <Dialog
        open={opened}
        onClose={onDialogClose}
        className={classNames(
          'fixed inset-0 overflow-y-auto items-center justify-center journey-theme-dark',
          'z-neue-cmdk'
        )}
        initialFocus={initialFocus}
      >
        <div className='flex items-center justify-center w-full h-full'>
          <animated.div style={styles}>
            <div
              className={classNames('relative flex rounded w-full mx-auto h-full', {
                'flex-col': layoutStyle === 'mobile',
              })}
            >
              <Dialog.Overlay className={classNames('fixed inset-0', overlayClassName)} onClick={onDialogClose} />
              <div
                className={`flex items-center relative h-auto m-auto bg-neue-journey-bg-90 text-white rounded-lg p-4`}
                style={{ width }}
              >
                <div className='flex flex-col w-full'>
                  <div className='flex flex-col gap-4'>
                    {subtitle && <div className='flex-1 text-neue-journey-small'>{subtitle}</div>}
                  </div>

                  <div className='flex flex-col gap-6 mt-6'>{children}</div>
                </div>
              </div>
            </div>
          </animated.div>
        </div>
      </Dialog>
    </Transition>
  );
};
